export interface NavigationItem {
  key: string;
  label: string;
  href: string;
  permission?: string;
}

export interface NavigationGroup {
  key: string;
  label: string;
  items: NavigationItem[];
}

export interface NavigationResponse {
  groups: NavigationGroup[];
  known_paths: string[];
  wildcard: boolean;
  permissions: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isNavigationItem(value: unknown): value is NavigationItem {
  if (!isRecord(value)) return false;
  return (
    typeof value.key === 'string' &&
    typeof value.label === 'string' &&
    typeof value.href === 'string' &&
    (value.permission === undefined || typeof value.permission === 'string')
  );
}

function isNavigationGroup(value: unknown): value is NavigationGroup {
  if (!isRecord(value)) return false;
  return (
    typeof value.key === 'string' &&
    typeof value.label === 'string' &&
    Array.isArray(value.items) &&
    value.items.every(isNavigationItem)
  );
}

/**
 * Validate the navigation contract before it reaches React render code.
 *
 * During a rolling release an older admin API may still return the legacy
 * `{ items, wildcard, permissions }` shape. Treat that as unavailable instead
 * of letting `for..of`/`flatMap` crash the entire admin console.
 */
export function parseNavigationResponse(value: unknown): NavigationResponse | null {
  if (!isRecord(value)) return null;
  if (!Array.isArray(value.groups) || !value.groups.every(isNavigationGroup)) return null;
  if (!isStringArray(value.known_paths) || !isStringArray(value.permissions)) return null;
  if (typeof value.wildcard !== 'boolean') return null;
  return value as unknown as NavigationResponse;
}
